/**
 * Blank clock faces — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_indic_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const blankClockFacesPack: ToilePack = {
    slug: 'blank-clock-faces',
    category: 'classroom',
    name: 'Blank clock faces',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'numbered', label: 'Numbered face', subject: 'a large clock face with the numbers 1 to 12 and minute marks and no hands'},
        {key: 'minutes', label: 'Minutes ring', subject: 'a clock face with the numbers 1 to 12 and an outer ring of minutes in fives'},
        {key: 'page', label: 'Six clocks', subject: 'a page of six small blank clock faces with a line under each for the time'},
    ],
    styles: [
        {key: 'outline', label: 'Black outline', style: 'a clean black-and-white classroom worksheet with bold outline circles and thick numerals', palette: 'black ink only', framing: 'the clock or clocks centered on the page inside an even margin'},
        {key: 'bright', label: 'Bright kids', style: 'a cheerful kids version with a coloured rim, coloured numerals and little stars', palette: 'primary red, yellow, blue and green on white', framing: 'the clock or clocks filling the page inside a margin'},
        {key: 'modern', label: 'Minimal modern', style: 'a minimal modern face with thin lines and fine sans numerals', palette: 'charcoal line on warm white', framing: 'the clock or clocks centered with a wide margin'},
    ],
    directions: ['roman numerals', '24 hour', 'large print'],
    directionHint: 'try: roman numerals',
    seo: {
        title: 'Blank Clock Face Printables: Face, Minutes Ring, Six Clocks',
        metaDescription: 'Nine printable blank clock faces for telling-time lessons: a numbered face without hands, a minutes ring and a page of six clocks, in three styles.',
        h1: 'Blank clock faces for telling the time: numbered face, minutes ring, six clocks',
        tagline: 'Nine pages in three styles, each style with a numbered face, a minutes ring and a page of six clocks.',
        intro: 'Teachers print blank clock faces for telling-time lessons. These nine pages give a numbered face with minute marks and no hands, a face with 1 to 12 and an outer ring of minutes marked in fives, and a page of six small clocks, each with a line under it for the time. Each comes in black outline, bright kids or minimal modern styles. Type a twist, such as Roman numerals or 24 hour, and the same layouts carry it.',
        useCases: [
            'a telling-time worksheet',
            'a classroom clock activity',
            'a homework sheet for a child learning the time',
            "a teacher's lesson pack",
        ],
        faq: [
            {q: 'Do the clocks have hands?', a: 'No. The face pages have no hands, so the child draws or places them.'},
            {q: 'What is on the minutes ring?', a: 'An outer ring of minutes, marked in fives, around the numbers 1 to 12.'},
            {q: 'Is there a page with several clocks?', a: 'Yes. One page has six small blank clocks, each with a line under it for the time.'},
            {q: 'What is the difference between the three styles?', a: 'Black outline is a black and white worksheet with bold circles and thick numerals. Bright kids has a coloured rim, coloured numerals and little stars. Minimal modern uses thin lines and fine sans numerals in charcoal.'},
        ],
    },
}
